import { useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button, cx, useFocusTrap } from '../charte';

/**
 * Fenêtre de l'espace élève (charte) : monte du bas sur téléphone, centrée sur ordinateur.
 * Échap, clic à côté et croix ferment ; focus clavier gardé dans la fenêtre.
 */
export function Sheet({
  title,
  description,
  onClose,
  children,
  footer,
  className,
  closeButton = true,
}: {
  title: ReactNode;
  description?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  /** Croix « Fermer » de l'en-tête ; sans elle, le pied de la fenêtre porte le seul bouton (« OK »). */
  closeButton?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useFocusTrap(true, ref, onClose);
  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-end justify-center font-sans sm:items-center sm:p-4">
      <div className="absolute inset-0 bg-heading/30 animate-v2-fade-in" onClick={onClose} aria-hidden />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cx(
          'sheet-panel relative flex max-h-[92dvh] w-full flex-col rounded-t-card bg-white shadow-pop outline-none sm:max-w-md sm:rounded-card',
          className,
        )}
      >
        <span className="mx-auto mt-2.5 h-1 w-10 shrink-0 rounded-pill bg-line sm:hidden" aria-hidden />
        <header className="flex shrink-0 items-start gap-3 px-6 pb-2 pt-4 sm:pt-6">
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="text-title-sm text-heading">
              {title}
            </h2>
            {description && <p className="mt-1 text-[13px] leading-relaxed text-secondary">{description}</p>}
          </div>
          {closeButton && <Button variant="ghost" size="sm" iconOnly icon={X} aria-label="Fermer" onClick={onClose} className="-mr-2" />}
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-2">{children}</div>
        {footer && <footer className="flex shrink-0 gap-2 border-t border-slate-100 px-6 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
